import type {
    SketchParametricOperationIntentCompileContext,
    SketchParametricOperationIntentCompilerContract,
} from '../../../../types/sketch/solver/SketchParametricOperationIntentCompiler';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';

/**
 * 参数化操作意图公式编译器。
 *
 * 本类把一次交互的直接输入转换为瞬态偏好方程。偏好只在全部硬关系、driving 尺寸、
 * Fix 与外部边界的可行域内优化，不修改关系/尺寸领域数据，也不写入持久化快照。
 */
export class SketchParametricOperationIntentCompiler
implements SketchParametricOperationIntentCompilerContract {
    /**
     * 编译操作意图。
     *
     * @param context 编译上下文。
     */
    public compile(context: SketchParametricOperationIntentCompileContext): void {
        if (context.intent.kind === 'entity-radius-target') {
            this.compileEntityRadiusTarget(context);
            return;
        }
        if (context.intent.kind === 'entity-line-length-target') {
            this.compileEntityLineLengthTarget(context);
            return;
        }

        const targets = context.intent.kind === 'point-target'
            ? [{pointId: context.intent.pointId, localPosition: context.intent.localPosition}]
            : context.intent.targets;
        const resolvedTargets = targets.map((target) => ({
            ...target,
            handle: context.pointHandles.get(target.pointId),
        }));
        const invalidTarget = resolvedTargets.find((target) => (
            !target.handle
            || !Number.isFinite(target.localPosition[0])
            || !Number.isFinite(target.localPosition[1])
        ));
        const pointIds = new Set(resolvedTargets.map((target) => target.pointId));
        const hasDuplicatePoint = pointIds.size !== resolvedTargets.length;

        if (resolvedTargets.length === 0 || invalidTarget || hasDuplicatePoint) {
            context.unsupportedIntents.push({
                id: context.intent.intentId,
                kind: context.intent.kind,
                reason: invalidTarget
                    ? `拖拽意图目标无效：${invalidTarget.pointId}`
                    : hasDuplicatePoint
                        ? '拖拽意图包含重复目标点'
                        : '拖拽意图没有目标点',
            });
            return;
        }

        const equationId = `intent:${context.intent.intentId}`;

        resolvedTargets.forEach((target, index) => {
            const fixedTarget = context.system.addPoint(
                `${equationId}:target:${index}`,
                target.localPosition[0],
                target.localPosition[1],
                true,
            );

            const preferenceEquation = SketchParametricEquationFactory.pointTargetPreference(
                `${equationId}:point:${index}`,
                target.handle!,
                fixedTarget,
            );

            context.system.addEquation(context.intent.isDirectManipulation === false
                // 系统稳定偏好不可达时必须回退硬约束解，不能复用直接拖拽的 blocked 语义。
                ? {...preferenceEquation, preferenceSeedBindings: []}
                : preferenceEquation);
        });

        this.compileTemporaryConstraints(context, equationId);
    }

    /**
     * 编译属性面板输入的圆系实体半径目标。
     *
     * @param context 操作意图编译上下文。
     */
    private compileEntityRadiusTarget(context: SketchParametricOperationIntentCompileContext): void {
        if (context.intent.kind !== 'entity-radius-target') {
            throw new Error('当前操作意图不是实体半径目标，无法编译。');
        }

        const radiusParameterId = context.radiusParameterIds.get(context.intent.entityId);
        if (!radiusParameterId
            || !Number.isFinite(context.intent.targetRadius)
            || context.intent.targetRadius <= 0) {
            context.unsupportedIntents.push({
                id: context.intent.intentId,
                kind: context.intent.kind,
                reason: !radiusParameterId
                    ? `半径目标无法解析圆系实体：${context.intent.entityId}`
                    : `半径目标值无效：${String(context.intent.targetRadius)}`,
            });
            return;
        }

        const fixedTargetParameterId = context.system.addFixedRadiusTargetParameter(
            context.intent.intentId,
            context.intent.targetRadius,
        );
        context.system.addEquation(SketchParametricEquationFactory.entityRadiusTargetPreference(
            `intent:${context.intent.intentId}:radius`,
            radiusParameterId,
            fixedTargetParameterId,
        ));
    }

    /**
     * 编译属性面板输入的直线长度目标。
     *
     * 长度是两个端点的派生几何量，不新增持久参数；偏好方程在全部正式关系的
     * 可行域内驱动端点。当目标属于阵列实例时，Patterned 硬关系会把变化反向
     * 传播到种子线，再同步到其余实例。
     *
     * @param context 操作意图编译上下文。
     */
    private compileEntityLineLengthTarget(context: SketchParametricOperationIntentCompileContext): void {
        if (context.intent.kind !== 'entity-line-length-target') {
            throw new Error('当前操作意图不是直线长度目标，无法编译。');
        }

        const lineHandles = context.linePointHandles.get(context.intent.entityId);
        if (!lineHandles
            || !Number.isFinite(context.intent.targetLength)
            || context.intent.targetLength <= 0) {
            context.unsupportedIntents.push({
                id: context.intent.intentId,
                kind: context.intent.kind,
                reason: !lineHandles
                    ? `直线长度目标无法解析直线实体：${context.intent.entityId}`
                    : `直线长度目标值无效：${String(context.intent.targetLength)}`,
            });
            return;
        }

        const lengthEquation = SketchParametricEquationFactory.pointPointDistance(
            `intent:${context.intent.intentId}:line-length`,
            lineHandles[0],
            lineHandles[1],
            context.intent.targetLength,
        );
        context.system.addEquation({
            ...lengthEquation,
            kind: 'operation-entity-line-length-target',
            strength: 'preference',
        });
    }

    /**
     * 编译 FreeCAD 式低优先级临时拖拽约束。
     *
     * 临时约束不进入关系领域或历史；它们仅与当前鼠标目标一起进入偏好层，
     * 并由分层求解器在正式硬约束的可行域内尝试精确满足。
     *
     * @param context 操作意图编译上下文。
     * @param equationId 当前操作意图的方程 ID 前缀。
     */
    private compileTemporaryConstraints(
        context: SketchParametricOperationIntentCompileContext,
        equationId: string,
    ): void {
        const constraints = context.intent.temporaryConstraints ?? [];
        const constraintIds = new Set<string>();

        constraints.forEach((constraint, index) => {
            if (constraintIds.has(constraint.constraintId)) {
                context.unsupportedIntents.push({
                    id: context.intent.intentId,
                    kind: constraint.kind,
                    reason: `临时拖拽约束 ID 重复：${constraint.constraintId}`,
                });
                return;
            }
            constraintIds.add(constraint.constraintId);

            if (constraint.kind === 'point-position') {
                const pointHandle = context.pointHandles.get(constraint.pointId);
                if (!pointHandle
                    || !Number.isFinite(constraint.targetPosition[0])
                    || !Number.isFinite(constraint.targetPosition[1])) {
                    context.unsupportedIntents.push({
                        id: context.intent.intentId,
                        kind: constraint.kind,
                        reason: `临时点位置约束无效：${constraint.pointId}`,
                    });
                    return;
                }

                const fixedPoint = context.system.addPoint(
                    `${equationId}:temporary-target:${constraint.constraintId}:${index}`,
                    constraint.targetPosition[0],
                    constraint.targetPosition[1],
                    true,
                );
                const coordinateBindings = [
                    [pointHandle.xId, fixedPoint.xId, 'x'],
                    [pointHandle.yId, fixedPoint.yId, 'y'],
                ] as const;

                coordinateBindings.forEach(([pointParameterId, targetParameterId, coordinate]) => {
                    const pointPositionEquation = SketchParametricEquationFactory.coordinateCoincident(
                        `${equationId}:temporary:${constraint.constraintId}:${index}:${coordinate}`,
                        pointParameterId,
                        targetParameterId,
                        'preference',
                    );
                    context.system.addEquation({
                        ...pointPositionEquation,
                        kind: 'temporary-drag-point-position',
                    });
                });
                return;
            }

            const lineHandles = context.linePointHandles.get(constraint.entityId);
            if (!lineHandles) {
                context.unsupportedIntents.push({
                    id: context.intent.intentId,
                    kind: constraint.kind,
                    reason: `临时直线约束无法解析实体：${constraint.entityId}`,
                });
                return;
            }

            if (constraint.kind === 'line-midpoint') {
                if (!Number.isFinite(constraint.targetPosition[0])
                    || !Number.isFinite(constraint.targetPosition[1])) {
                    context.unsupportedIntents.push({
                        id: context.intent.intentId,
                        kind: constraint.kind,
                        reason: `临时直线中点约束无效：${constraint.entityId}`,
                    });
                    return;
                }

                const fixedMidpoint = context.system.addPoint(
                    `${equationId}:temporary-target:${constraint.constraintId}:${index}`,
                    constraint.targetPosition[0],
                    constraint.targetPosition[1],
                    true,
                );
                const midpointEquation = SketchParametricEquationFactory.midpoint(
                    `${equationId}:temporary:${constraint.constraintId}:${index}`,
                    fixedMidpoint,
                    lineHandles[0],
                    lineHandles[1],
                );

                context.system.addEquation({
                    ...midpointEquation,
                    kind: 'temporary-drag-line-midpoint',
                    strength: 'preference',
                });
                return;
            }

            if (!Number.isFinite(constraint.targetLength) || constraint.targetLength <= 0) {
                context.unsupportedIntents.push({
                    id: context.intent.intentId,
                    kind: constraint.kind,
                    reason: `临时直线长度约束无效：${constraint.entityId}`,
                });
                return;
            }

            const lengthEquation = SketchParametricEquationFactory.pointPointDistance(
                `${equationId}:temporary:${constraint.constraintId}:${index}`,
                lineHandles[0],
                lineHandles[1],
                constraint.targetLength,
            );

            // 只改变方程层级和诊断类型，几何量继续复用统一两点距离事实源。
            context.system.addEquation({
                ...lengthEquation,
                kind: 'temporary-drag-line-length',
                strength: 'preference',
            });
        });
    }
}
